import React, { useEffect, useState } from "react";
import { X, User, ChevronUp } from "lucide-react";
import type { ArchivedConversation } from "./types";
import { formatArchiveDate } from "./types";
import { ActionsTab, ActivityTimeline, DealStageDropdown, InsightDataTab, useConversationInsights, useDealProgress } from "../../../../components/crm/conversation/components/insight";
import type { ConversationInsights } from "../types";

type PreviewTab = "Actions" | "Insight" | "Details";

const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  closed: "Closed",
};

const STATUS_STYLE: Record<string, string> = {
  open: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  closed: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

const CollapsibleSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-gray-100 dark:border-gray-800">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-transparent"
      >
        <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">{title}</span>
        <ChevronUp size={16} className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${open ? "" : "rotate-180"}`} />
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 gap-2">
    <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-800 dark:text-gray-100 text-right truncate">{value || "—"}</span>
  </div>
);

const DetailsPane: React.FC<{
  conversation: ArchivedConversation;
  insights: ConversationInsights | null;
  loadingInsights: boolean;
  dealProgress: ReturnType<typeof useDealProgress>["dealProgress"];
  loadingDealProgress: boolean;
  updatingStage: boolean;
  updateStage: ReturnType<typeof useDealProgress>["updateStage"];
}> = ({ conversation, insights, loadingInsights, dealProgress, loadingDealProgress, updatingStage, updateStage }) => {
  return (
    <>
      <CollapsibleSection title="Contact Info">
        <InfoRow label="Full Name" value={conversation.contact?.name} />
        <InfoRow label="Phone Number" value={conversation.contact?.phone} />
        <InfoRow label="Email" value={conversation.contact?.email} />
        <InfoRow label="Assigned By" value={conversation.assigned_by} />
      </CollapsibleSection>
      <CollapsibleSection title="Activity Timeline">
        <ActivityTimeline timeline={insights?.timeline} loading={loadingInsights} />
      </CollapsibleSection>
      <CollapsibleSection title="Deal Progress">
        {loadingDealProgress && !dealProgress ? (
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 py-2">Loading deal progress…</p>
        ) : dealProgress ? (
          <>
            <InfoRow label="Overall Progress" value={`${dealProgress.progress_percent}%`} />
            <div className="flex flex-col mt-2">
              {[...dealProgress.stages].reverse().map((stage, i, arr) => (
                <div key={stage.key} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => !updatingStage && !stage.current && updateStage(stage.key)}
                      disabled={updatingStage || stage.current}
                      title={stage.current ? undefined : `Move to ${stage.label}`}
                      className={`w-2.5 h-2.5 rounded-full shrink-0 mt-0.5 p-0 border-none ${
                        stage.completed ? "bg-emerald-500" : "border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                      } ${stage.current ? "ring-2 ring-emerald-200 dark:ring-emerald-500/30" : "cursor-pointer"}`}
                    />
                    {i < arr.length - 1 && <span className="w-px flex-1 bg-gray-200 dark:bg-gray-700 my-0.5" />}
                  </div>
                  <div className="pb-3 min-w-0">
                    <p className={`text-xs font-medium leading-tight ${stage.current ? "text-gray-800 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}`}>
                      {stage.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Deal tracking isn't available yet.</p>
        )}
      </CollapsibleSection>
    </>
  );
};

const ConversationPreviewModal: React.FC<{
  conversation: ArchivedConversation | null;
  onClose: () => void;
  onResume: (conversation: ArchivedConversation) => void;
  resuming?: boolean;
}> = ({ conversation, onClose, onResume, resuming }) => {
  const [tab, setTab] = useState<PreviewTab>("Actions");
  const { insights, loadingInsights } = useConversationInsights(conversation?.id);
  const { dealProgress, loadingDealProgress, updatingStage, updateStage } = useDealProgress(conversation?.id);

  useEffect(() => {
    setTab("Actions");
  }, [conversation?.id]);

  if (!conversation) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl h-[560px] flex overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2.5 right-2.5 z-10 p-1.5 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-[#7B7B7B] font-semibold dark:text-gray-500"
        >
          <X size={16} />
        </button>

        {/* Left: conversation summary */}
        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-800/60 min-w-0">
          <div className="flex items-center justify-between gap-3 px-5 py-4 bg-emerald-50 dark:bg-emerald-500/10 border-b border-emerald-100 dark:border-emerald-500/20">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center shrink-0">
                <User size={18} className="text-indigo-500 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{conversation.contact?.name}</p>
                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{conversation.contact?.phone}</p>
              </div>
            </div>
            <DealStageDropdown currentStage={dealProgress?.current_stage} updating={updatingStage} onChange={updateStage} />
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div>
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1">Recent Message</p>
              <div className="bg-white dark:bg-gray-800 rounded-2xl rounded-tl-none shadow-sm px-4 py-2.5">
                <p className="text-sm text-gray-800 dark:text-gray-100">{conversation.recent_message || "—"}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Start: {formatArchiveDate(conversation.start_date)}</span>
              <span>End: {formatArchiveDate(conversation.end_date)}</span>
            </div>

            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                STATUS_STYLE[conversation.status] || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
              }`}
            >
              {STATUS_LABEL[conversation.status] || conversation.status}
            </span>
          </div>

          <div className="px-5 py-3 shrink-0">
            <button
              onClick={() => onResume(conversation)}
              disabled={resuming}
              className="text-sm font-medium text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resuming ? "Restoring…" : "Resume Conversation"}
            </button>
          </div>
        </div>

        {/* Right: Details / Insight / Actions */}
        <div className="w-[300px] shrink-0 bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800 flex flex-col">
          <div className="flex border-b border-gray-100 dark:border-gray-800 shrink-0 pr-9">
            {(["Actions", "Insight", "Details"] as PreviewTab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  tab === t ? "text-gray-900 dark:text-gray-100 border-b-2 border-gray-900 dark:border-gray-100" : "text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {tab === "Details" && (
              <DetailsPane
                conversation={conversation}
                insights={insights}
                loadingInsights={loadingInsights}
                dealProgress={dealProgress}
                loadingDealProgress={loadingDealProgress}
                updatingStage={updatingStage}
                updateStage={updateStage}
              />
            )}
            {tab === "Insight" && <InsightDataTab insights={insights} loadingInsights={loadingInsights} />}
            {tab === "Actions" && <ActionsTab insights={insights} loadingInsights={loadingInsights} conversationId={conversation.id} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationPreviewModal;
